import React from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import {
  Box,
  Button,
  IconButton,
  Typography,
  MenuItem,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@shared/components/TextField';
import { ServiceCreateOrUpdateData } from '../entity';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

export type WorkPauseBlock = {
  type: 'work' | 'wait';
  duration: number;
};

type AddWorkPauseBlockProps = {
  id?: string;
  control: Control<ServiceCreateOrUpdateData>;
  fieldPath: 'blocks';
  defaultValues?: WorkPauseBlock[];
};

const AddWorkPauseBlock: React.FC<AddWorkPauseBlockProps> = ({
  id,
  control,
  fieldPath,
  defaultValues = [],
}) => {
  const { t } = useIntlTranslations();
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldPath,
  });

  const watchedFields = useWatch({
    control,
    name: fieldPath,
    defaultValue: defaultValues,
  });

  const handleAddBlock = () => {
    append({ type: 'work', duration: 30 });
  };

  const getBlockTypeColor = (type: 'work' | 'wait') => {
    return type === 'work' ? 'primary.light' : 'warning.light';
  };

  return (
    <Box id={id}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">
          {t('workPauseBlock.title')}
        </Typography>
        <Button
          id="service-work-block-button"
          startIcon={<AddIcon />}
          onClick={handleAddBlock}
          variant="outlined"
          size="small"
        >
          {t('workPauseBlock.addBlock')}
        </Button>
      </Stack>

      {fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          {t('workPauseBlock.noBlocks')}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {fields.map((field, index) => {
            const currentBlock = watchedFields[index] || { type: 'work', duration: 30 };
            return (
              <Card
                key={field.id}
                variant="outlined"
                sx={{
                  bgcolor: getBlockTypeColor(currentBlock.type),
                  borderColor: getBlockTypeColor(currentBlock.type),
                }}
              >
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2" alignContent="center">
                      {index + 1}.
                    </Typography>

                    <TextField
                      control={control}
                      fieldPath={`${fieldPath}.${index}.type`}
                      label={t('workPauseBlock.type')}
                      sx={{ width: 120 }}
                      select
                      size="small"
                    >
                      <MenuItem value="work">{t('workPauseBlock.work')}</MenuItem>
                      <MenuItem value="wait">{t('workPauseBlock.wait')}</MenuItem>
                    </TextField>

                    <TextField
                      control={control}
                      fieldPath={`${fieldPath}.${index}.duration`}
                      label={t('workPauseBlock.duration')}
                      sx={{ width: 90 }}
                      type="number"
                      size="small"
                    />

                    <IconButton onClick={() => remove(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {fields.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="workPauseBlock.summary"
              defaultMessage="Celková doba: {totalDuration} minut"
              values={{
                totalDuration: watchedFields.reduce(
                  (sum, block) => sum + (block?.duration || 0),
                  0,
                ),
              }}
            />
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AddWorkPauseBlock;
